import {GroupByGroupsQueryManager, GroupByGroupStore} from '@modules'
import {GroupOrientation, StoresInstance} from '@stores'
import {useVipLocalStore, VipLocalStore} from '@modules'
import {action, computed, observable, _} from '@api'
import {VLP_FOLDER_LENGTH_RUNTIME_MAPPING, VLP_FOLDER_LENGTH_RUNTIME_MAPPING_ID} from '@stores/vlp'
import {trace} from 'mobx'

export class ChildGroupsStore extends VipLocalStore {
  constructor(stores: StoresInstance, public parentGroupStore?: GroupByGroupStore) {
    super(stores)
    this.mgr = new GroupByGroupsQueryManager(stores, parentGroupStore)
  }

  mgr: GroupByGroupsQueryManager

  @observable orientation: GroupOrientation = 'vertical'
  @action toggleOrientation = () => this.orientation = this.orientation == 'horizontal' ? 'vertical' : 'horizontal'

  @computed get isCard() { return this.groupBy.settings.groupView == 'card' && this.grouping?.allowCardMode }

  // region Properties

  get level() {return this.parentGroupStore.props.level + 1}

  get groupName() {
    const {parentGroupStore: {props: {level}}, catalog: {groupBy: {hierarchy}}} = this

    return level + 1 >= hierarchy.length ? null : hierarchy[level + 1]
  }

  @computed get grouping() {
    return this.groupBy.hierarchyGroups[this.level]
  }

  @computed get shouldRenderAsCssGrid() {
    const {isCard, parentGroupStore: {isFlat, esInfo}, grouping: {fieldDefinition}, groupBy: {settings: {groupView}}} = this

    return !isCard && ((groupView == 'card' && !esInfo) || isFlat || !fieldDefinition?.esIndex)
  }
  @computed get childGroups() {
    const {path, mgr, grouping} = this
    return !grouping ? [] : mgr.data.map(({id}) => grouping.nodes.get(grouping.pathLookup(id, path)))
  }

  @computed get id() {
    const {groupName} = this
    if (!groupName) return null

    const s = groupName.split(':')
    return s.length == 1 ? s[0] : s[1]
  }

  @computed get title() {
    const {grouping, groupName} = this
    if (!groupName) return null

    return grouping.def?.displayName?.plural ?? grouping.fieldDefinition?.label ?? groupName
  }

  // endregion

  // region Expand/Collapse All

  @computed get isCollapsedAll() {
    return this.childGroups.every(g => !g?.store?.isExpanded)
  }

  @computed get isExpandedAll() {
    return this.childGroups.every(g => g?.store?.isExpanded && (!g.store.hasChildGroup || g.store.childGroupStore.isExpandedAll))
  }

  @action collapseAllChildren = () => {
    this.childGroups.forEach(({store}) => store.collapse())
  }
  @action expandAllChildren = () => {
    this.childGroups.forEach(({store}) => store.expand())
  }

  @action toggleExpandAll = () => {
    const {isExpandedAll, childGroups} = this
    childGroups.forEach(({store: {isExpanded, toggleExpanded}}) => isExpanded == isExpandedAll && toggleExpanded())
  }

  // endregion

  @computed get path(): string[] {
    const {parentGroupStore: {props: {path, info}}} = this
    return info ? [...(path ?? []), info.key.toString()] : path
  }

  @action toggleExpanded = (id) => () => {
    const {path, grouping} = this
    grouping.toggleExpanded(id, path)
  }

  @computed get productQueryVariables() {
    const { catalog, parentGroupStore: { mustTerms, mustNotTerms, grouping, isUnassigned }, catalog: {groupBy: { catalogItemsGroup }}} = this
    let esFilter = _.cloneDeep(catalog.queryVariablesEs)

    if (catalogItemsGroup?.functionScoreTerm) {
      !_.isEmpty(mustTerms) && esFilter.query?.function_score?.query?.bool.filter.push(...mustTerms)
      !_.isEmpty(mustNotTerms) && esFilter.query?.function_score?.query?.bool.filter.push({ bool: { must_not: mustNotTerms } })
    } else if (esFilter?.query) {
      !_.isEmpty(mustTerms) && esFilter.query.bool.filter.push(...mustTerms)
      !_.isEmpty(mustNotTerms) && esFilter.query.bool.filter.push({ bool: { must_not: mustNotTerms } })
    }

    if (isUnassigned) {
      if (grouping.vlpType?.type === 'folder') {
        // Special case - we need to exclude catalog items which have a subCategory array that doesn't include our group level

        esFilter.query.bool.filter.push({bool: {must_not: {exists: {field: 'subCategories'}}}})
      }
    }

    return esFilter
  }
}



// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByCatalog_Groups/GroupByCatalog_LoadMore/ChildGroupsStore.tsx